/**
 * JournifiSync Import Modal — v7.5.14 visual refresh.
 *
 * Surfaces ALL the NT-bridge configuration in one place:
 *   - Sync status hero (NT connection, auto-import state, last activity)
 *   - Settings (folder path, auto-start, sound)
 *   - Activity log (recent trades + sync events)
 *   - Accounts (filter + mapping + NT-AddOn blocklist, read-only)
 *   - Manual one-off import (date range + preview + import)
 *
 * Visual style matches AccountManagementModal: rounded-2xl, frosted-glass
 * dark mode (`bg-gray-950/90 backdrop-blur-2xl`), `transition-colors
 * duration-300`, pill status badges. Wider at `max-w-4xl` so the Settings +
 * Activity columns can sit side-by-side on lg screens.
 *
 * Functional adds this revision:
 *   1. Account Filter `include` / `exclude` mode toggle — matches Chris's
 *      "blocker list" mental model. Persists in localStorage.
 *   2. Search box for the Account Filter + Account Mapping rows — useful
 *      with 20+ NT accounts in the dropdown.
 *   3. Read-only display of the C# AddOn's `disabledAccounts` so it's
 *      obvious which accounts are blocked at the NT level (the discovery
 *      that opened the v7.5.14 Sim101 investigation).
 */

import { useState, useEffect, useMemo } from 'react';
import {
  X, RefreshCw, Download, AlertCircle, AlertTriangle, CheckCircle, Folder,
  Calendar, ChevronDown, ChevronUp, Zap, ZapOff, Users, Settings, Link2,
  Plus, Trash2, Search, ShieldOff, Activity, Volume2, VolumeX,
} from 'lucide-react';
import {
  checkSyncFolder, importFromJournifiSync, previewJournifiSyncImport,
  getAvailableAccounts,
} from '../../services/journifiSyncService';
import GlassSelect from '../Common/GlassSelect';

// ── localStorage keys for persistent modal state ────────────────────────────
const ACCOUNT_FILTER_KEY = 'journifi.journifiSync.selectedAccounts';
const FILTER_MODE_KEY    = 'journifi.journifiSync.filterMode';     // 'include' | 'exclude'

// Path to the C# AddOn's settings file. We read this to display the
// AddOn-side `disabledAccounts` list as a read-only diagnostic (the user
// edits it inside NT, not here, but seeing what's blocked is the first
// step to debugging "why isn't this account syncing?" — the discovery that
// opened the v7.5.14 Sim101 investigation).
const ADDON_SETTINGS_PATH = 'C:\\NinjaTrader8Data\\Journifi\\settings.json';

const loadSelectedAccounts = () => {
  try {
    const raw = localStorage.getItem(ACCOUNT_FILTER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string') : [];
  } catch {
    return [];
  }
};

const saveSelectedAccounts = (accounts) => {
  try {
    localStorage.setItem(ACCOUNT_FILTER_KEY, JSON.stringify(Array.isArray(accounts) ? accounts : []));
  } catch {
    // localStorage quota / privacy mode — fall through silently.
  }
};

const loadFilterMode = () => {
  try {
    const v = localStorage.getItem(FILTER_MODE_KEY);
    return v === 'exclude' ? 'exclude' : 'include';
  } catch {
    return 'include';
  }
};

const saveFilterMode = (mode) => {
  try { localStorage.setItem(FILTER_MODE_KEY, mode === 'exclude' ? 'exclude' : 'include'); } catch { /* no-op */ }
};

const JournifiSyncModal = ({
  darkMode,
  accounts,
  existingTrades,
  onClose,
  onImport,
  onCreateAccounts,
  onRepairTrades,
  journifiSync,
}) => {
  // ── core state ────────────────────────────────────────────────────────────
  const [syncStatus, setSyncStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [sessionImportedTrades, setSessionImportedTrades] = useState([]);

  // ── accounts state ────────────────────────────────────────────────────────
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccountsState] = useState(loadSelectedAccounts);
  const [filterMode, setFilterModeState] = useState(loadFilterMode);  // 'include' | 'exclude'
  const [accountSearchQuery, setAccountSearchQuery] = useState('');

  // ── AddOn diagnostic (read-only) ──────────────────────────────────────────
  const [addonDisabledAccounts, setAddonDisabledAccounts] = useState([]);
  const [addonSettingsLoaded, setAddonSettingsLoaded] = useState(false);

  // ── UI section visibility ─────────────────────────────────────────────────
  const [showAccountFilter, setShowAccountFilter] = useState(false);
  const [showAccountMapping, setShowAccountMapping] = useState(false);
  const [showFolderConfig, setShowFolderConfig] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(true);
  const [manualNtAccount, setManualNtAccount] = useState('');
  const [tempFolderPath, setTempFolderPath] = useState(journifiSync?.syncFolder || '');

  // Wrap setters so every write also persists.
  const setSelectedAccounts = (next) => {
    setSelectedAccountsState(prev => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      saveSelectedAccounts(resolved);
      return resolved;
    });
  };
  const setFilterMode = (mode) => {
    setFilterModeState(mode);
    saveFilterMode(mode);
  };

  // Auto-expand Account Mapping section when unmapped NT accounts are detected.
  const unmappedCount = journifiSync?.unmappedAccounts?.length || 0;
  useEffect(() => {
    if (unmappedCount > 0) setShowAccountMapping(true);
  }, [unmappedCount]);

  // ── effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    checkSyncStatus();
  }, [journifiSync?.syncFolder]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (journifiSync?.syncFolder) setTempFolderPath(journifiSync.syncFolder);
  }, [journifiSync?.syncFolder]);

  // Load AddOn settings on mount to display the disabledAccounts diagnostic.
  // Re-reads on folder change in case the user reconfigured the AddOn path
  // (rare but the diagnostic should reflect reality).
  useEffect(() => {
    const load = async () => {
      if (!window.electronAPI?.journifiSync?.readFile) {
        setAddonSettingsLoaded(true);
        return;
      }
      try {
        const result = await window.electronAPI.journifiSync.readFile(ADDON_SETTINGS_PATH);
        if (result?.success && result.data) {
          const disabled = result.data?.sync?.disabledAccounts;
          setAddonDisabledAccounts(Array.isArray(disabled) ? disabled : []);
        }
      } catch {
        // File may not exist if the AddOn isn't installed. Silently no-op —
        // the diagnostic section just doesn't render its block-list row.
      } finally {
        setAddonSettingsLoaded(true);
      }
    };
    load();
  }, [journifiSync?.syncFolder]);

  const checkSyncStatus = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const status = await checkSyncFolder(journifiSync?.syncFolder);
      setSyncStatus(status);
      if (status.exists) {
        const accountsResult = await getAvailableAccounts(journifiSync?.syncFolder);
        if (accountsResult.success) setAvailableAccounts(accountsResult.accounts);
      }
    } catch (err) {
      setError(err.message);
    }
    setIsLoading(false);
  };

  // ── derived values ────────────────────────────────────────────────────────

  // Translate the (mode, selectedAccounts) UI state into the actual list of
  // account names to pass to the import service.
  //   - include mode + non-empty selection → those exact names
  //   - exclude mode + non-empty selection → all available MINUS those names
  //   - empty selection                     → null (import all, historical default)
  const effectiveSelectedAccounts = useMemo(() => {
    if (selectedAccounts.length === 0) return null;
    if (filterMode === 'include') return selectedAccounts;
    // exclude
    return availableAccounts.filter(a => !selectedAccounts.includes(a));
  }, [selectedAccounts, availableAccounts, filterMode]);

  // Apply the search filter to the visible accounts list (filter UI). Sort
  // alphabetically for stable scanning.
  const filteredAvailableAccounts = useMemo(() => {
    const q = accountSearchQuery.trim().toLowerCase();
    const sorted = [...availableAccounts].sort();
    if (!q) return sorted;
    return sorted.filter(a => a.toLowerCase().includes(q));
  }, [availableAccounts, accountSearchQuery]);

  // Merge available + mapped + unmapped NT account names for the mapping rows.
  const allMappableAccounts = useMemo(() => {
    const mappedKeys = Object.keys(journifiSync?.accountMappings || {});
    const unmappedList = journifiSync?.unmappedAccounts || [];
    return [...new Set([...availableAccounts, ...mappedKeys, ...unmappedList])].sort();
  }, [availableAccounts, journifiSync?.accountMappings, journifiSync?.unmappedAccounts]);

  const filteredMappableAccounts = useMemo(() => {
    const q = accountSearchQuery.trim().toLowerCase();
    if (!q) return allMappableAccounts;
    return allMappableAccounts.filter(a => a.toLowerCase().includes(q));
  }, [allMappableAccounts, accountSearchQuery]);

  // ── handlers ──────────────────────────────────────────────────────────────
  const handlePreview = async () => {
    setIsLoading(true);
    setError(null);
    setPreview(null);
    try {
      const allExistingTrades = [...existingTrades, ...sessionImportedTrades];
      const result = await previewJournifiSyncImport({
        folderPath: journifiSync?.syncFolder,
        startDate: dateRange.startDate || null,
        endDate: dateRange.endDate || null,
        selectedAccounts: effectiveSelectedAccounts,
        accountMappings: journifiSync?.accountMappings || {},
        existingAccounts: accounts,
        existingTrades: allExistingTrades,
      });
      setPreview(result);
      if (result.errors.length > 0) setError(result.errors.join('\n'));
    } catch (err) {
      setError(err.message);
    }
    setIsLoading(false);
  };

  const handleImport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const allExistingTrades = [...existingTrades, ...sessionImportedTrades];
      const result = await importFromJournifiSync({
        folderPath: journifiSync?.syncFolder,
        startDate: dateRange.startDate || null,
        endDate: dateRange.endDate || null,
        selectedAccounts: effectiveSelectedAccounts,
        accountMappings: journifiSync?.accountMappings || {},
        existingAccounts: accounts,
        existingTrades: allExistingTrades,
      });
      setImportResult(result);
      if (result.errors.length > 0) setError(result.errors.join('\n'));
      if (result.newAccounts.length > 0 && onCreateAccounts) onCreateAccounts(result.newAccounts);
      if (result.trades.length > 0 && onImport) {
        onImport(result.trades);
        setSessionImportedTrades(prev => [...prev, ...result.trades]);
        setPreview(null);
      }
    } catch (err) {
      setError(err.message);
    }
    setIsLoading(false);
  };

  // ── styling helpers (dark-mode-aware) ─────────────────────────────────────
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-600';
  const textSubtle = darkMode ? 'text-gray-500' : 'text-gray-500';
  const cardBg = darkMode ? 'bg-white/[0.03] border-white/10' : 'bg-gray-50/60 border-gray-200';
  const cardBgHover = darkMode ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-100/60';
  const inputBg = darkMode ? 'bg-gray-800/60 border-white/10' : 'bg-white border-gray-300';
  const dividerBorder = darkMode ? 'border-white/10' : 'border-gray-200';

  // Section title row used by every card.
  const SectionTitle = ({ icon: Icon, color, label, badge, action, expandable, expanded, onToggle }) => (
    <button
      type="button"
      onClick={onToggle}
      disabled={!expandable}
      className={`w-full flex items-center gap-2.5 ${expandable ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-${color}-500/15 text-${color}-${darkMode ? '300' : '600'}`}>
        <Icon size={16} />
      </div>
      <span className={`text-sm font-semibold ${text}`}>{label}</span>
      {badge && (
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-${color}-500/15 text-${color}-${darkMode ? '300' : '700'}`}>
          {badge}
        </span>
      )}
      {action && <span className="ml-auto">{action}</span>}
      {expandable && !action && (
        <span className={`ml-auto ${textMuted}`}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      )}
    </button>
  );

  // Pill-style toggle (used for filter mode, etc.)
  const PillToggle = ({ options, value, onChange }) => (
    <div className={`inline-flex rounded-lg p-0.5 border ${darkMode ? 'bg-gray-800/60 border-white/10' : 'bg-gray-100 border-gray-300'}`}>
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors duration-150 ${
            value === opt.value
              ? darkMode ? 'bg-purple-500/30 text-purple-100' : 'bg-white text-purple-700 shadow-sm'
              : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  // Switch (used for boolean settings like auto-start, sound).
  const Switch = ({ checked, onChange, label, hint, icon: Icon }) => (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon size={14} className={textMuted} />}
        <div className="min-w-0">
          <div className={`text-sm ${text}`}>{label}</div>
          {hint && <div className={`text-[11px] ${textSubtle}`}>{hint}</div>}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-colors duration-200 shrink-0 ${
          checked ? 'bg-green-500' : darkMode ? 'bg-gray-700' : 'bg-gray-300'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );

  // ── derived: status hero state ────────────────────────────────────────────
  const ntConnected = journifiSync?.ninjatraderConnected;
  const syncActive = journifiSync?.isActive;
  const heroState = !syncStatus?.exists
    ? 'no-folder'
    : !syncActive
      ? 'paused'
      : !ntConnected
        ? 'no-nt'
        : 'live';
  const heroTone = {
    'live':       { glow: 'from-emerald-500/15 to-emerald-500/0',  ring: 'border-emerald-400/40', dot: 'bg-emerald-400',   text: 'text-emerald-300', label: 'Live · auto-importing trades', icon: Zap,    iconColor: 'text-emerald-400' },
    'no-nt':      { glow: 'from-amber-500/15   to-amber-500/0',    ring: 'border-amber-400/40',   dot: 'bg-amber-400',     text: 'text-amber-300',   label: 'Sync running · NinjaTrader disconnected', icon: AlertTriangle, iconColor: 'text-amber-400' },
    'paused':     { glow: 'from-gray-500/10    to-gray-500/0',     ring: 'border-gray-400/30',    dot: 'bg-gray-400',      text: 'text-gray-300',    label: 'Sync paused', icon: ZapOff, iconColor: 'text-gray-400' },
    'no-folder':  { glow: 'from-rose-500/15    to-rose-500/0',     ring: 'border-rose-400/40',    dot: 'bg-rose-400',      text: 'text-rose-300',    label: 'Sync folder not found', icon: AlertCircle, iconColor: 'text-rose-400' },
  }[heroState];
  const HeroIcon = heroTone.icon;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className={`rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden transition-colors duration-300 ${
        darkMode ? 'bg-gray-950/90 backdrop-blur-2xl border border-white/10' : 'bg-white border border-gray-200'
      }`}>
        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${dividerBorder}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className={`text-base font-bold ${text}`}>JournifiSync</h2>
              <p className={`text-xs ${textMuted}`}>NinjaTrader bridge · sync + import</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              darkMode ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
            }`}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── CONTENT (scrollable) ───────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* ── STATUS HERO ──────────────────────────────────────────────── */}
          <div className={`relative rounded-xl border p-4 overflow-hidden ${heroTone.ring} ${darkMode ? 'bg-white/[0.02]' : 'bg-gray-50/50'}`}>
            <div className={`absolute inset-0 bg-gradient-to-r ${heroTone.glow} pointer-events-none`} />
            <div className="relative flex items-center gap-3 flex-wrap">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${darkMode ? 'bg-white/5' : 'bg-white'} ${heroTone.iconColor}`}>
                <HeroIcon size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-block w-2 h-2 rounded-full ${heroTone.dot} animate-pulse`} />
                  <span className={`text-sm font-semibold ${heroTone.text}`}>{heroTone.label}</span>
                </div>
                <div className={`text-xs mt-0.5 ${textMuted}`}>
                  {syncStatus?.exists ? (
                    <>
                      <span className="font-mono">{syncStatus.fileCount}</span> execution file{syncStatus.fileCount === 1 ? '' : 's'} ·{' '}
                      <span className="font-mono">{availableAccounts.length}</span> NT account{availableAccounts.length === 1 ? '' : 's'} detected
                      {journifiSync?.recentTrades?.length > 0 && (
                        <> · <span className="font-mono">{journifiSync.recentTrades.length}</span> recent</>
                      )}
                    </>
                  ) : (
                    <>{syncStatus?.error || 'Checking sync folder…'}</>
                  )}
                </div>
              </div>
              {journifiSync && journifiSync.isElectron && syncStatus?.exists && (
                <button
                  onClick={journifiSync.toggle}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200 shrink-0 ${
                    syncActive
                      ? darkMode ? 'bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-400/30' : 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-300'
                      : darkMode ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-400/30' : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300'
                  }`}
                >
                  {syncActive ? 'Pause Sync' : 'Start Sync'}
                </button>
              )}
              <button
                onClick={checkSyncStatus}
                disabled={isLoading}
                className={`p-2 rounded-lg transition-colors duration-200 shrink-0 ${
                  darkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                }`}
                title="Refresh sync status"
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* ── SETTINGS + ACTIVITY (2-col on lg+) ───────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* SETTINGS CARD */}
            <div className={`rounded-xl border p-4 ${cardBg}`}>
              <SectionTitle icon={Settings} color="blue" label="Settings" />
              <div className={`mt-3 space-y-2 pt-2 border-t ${dividerBorder}`}>
                {/* Folder row — clickable to expand inline editor */}
                <button
                  type="button"
                  onClick={() => setShowFolderConfig(!showFolderConfig)}
                  className={`w-full flex items-center justify-between gap-2 py-1 text-left rounded ${cardBgHover}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Folder size={14} className={textMuted} />
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm ${text}`}>Sync folder</div>
                      <div className={`text-[11px] truncate font-mono ${textSubtle}`} title={journifiSync?.syncFolder}>
                        {journifiSync?.syncFolder || 'Not configured'}
                      </div>
                    </div>
                  </div>
                  {showFolderConfig ? <ChevronUp size={14} className={textMuted} /> : <ChevronDown size={14} className={textMuted} />}
                </button>
                {showFolderConfig && (
                  <div className={`pl-6 mt-1 mb-1 pb-3 border-b ${dividerBorder}`}>
                    <label className={`block text-[11px] mb-1 ${textMuted}`}>
                      Edit folder path (must match the AddOn&apos;s output folder)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={tempFolderPath}
                        onChange={(e) => setTempFolderPath(e.target.value)}
                        className={`flex-1 px-2.5 py-1.5 rounded-md text-xs font-mono border ${inputBg} ${text}`}
                        placeholder="C:\\NinjaTrader8Data\\JournifiSync"
                      />
                      <button
                        onClick={() => {
                          if (journifiSync?.setSyncFolder) {
                            journifiSync.setSyncFolder(tempFolderPath);
                            setShowFolderConfig(false);
                          }
                        }}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                          darkMode
                            ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70'
                            : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
                        }`}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}

                {/* Auto-start + sound toggles */}
                {journifiSync && journifiSync.isElectron && (
                  <>
                    <Switch
                      icon={Zap}
                      label="Auto-start sync on launch"
                      hint="Sync starts when the app opens"
                      checked={journifiSync.autoStartEnabled}
                      onChange={(v) => journifiSync.setAutoStartEnabled(v)}
                    />
                    <Switch
                      icon={journifiSync.soundEnabled ? Volume2 : VolumeX}
                      label="Sound on synced trade"
                      hint="Ping when a new trade is imported"
                      checked={journifiSync.soundEnabled}
                      onChange={(v) => journifiSync.setSoundEnabled(v)}
                    />
                  </>
                )}
              </div>
            </div>

            {/* ACTIVITY CARD */}
            <div className={`rounded-xl border p-4 ${cardBg}`}>
              <SectionTitle
                icon={Activity}
                color="emerald"
                label="Activity"
                badge={journifiSync?.recentTrades?.length > 0 ? `${journifiSync.recentTrades.length} recent` : null}
                expandable
                expanded={showActivityLog}
                onToggle={() => setShowActivityLog(!showActivityLog)}
              />
              {showActivityLog && (
                <div className={`mt-3 pt-3 border-t ${dividerBorder} space-y-3`}>
                  {/* Recent trades */}
                  {journifiSync?.recentTrades?.length > 0 ? (
                    <div className="space-y-1">
                      <div className={`text-[10px] uppercase tracking-wider ${textMuted}`}>Recent trades</div>
                      {journifiSync.recentTrades.slice(0, 3).map((trade, idx) => (
                        <div key={idx} className={`flex justify-between items-center text-xs px-2 py-1 rounded ${darkMode ? 'bg-white/[0.03]' : 'bg-white'}`}>
                          <span className={text}>
                            <span className="font-mono">{trade.symbol}</span>
                            <span className={`ml-1.5 ${trade.direction === 'long' ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {trade.direction === 'long' ? '↑' : '↓'}
                            </span>
                          </span>
                          <span className={`font-mono ${parseFloat(trade.pnl) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {parseFloat(trade.pnl) >= 0 ? '+' : '−'}${Math.abs(parseFloat(trade.pnl)).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-xs italic ${textSubtle}`}>
                      No trades synced this session.
                    </div>
                  )}

                  {/* Activity log */}
                  {journifiSync?.syncLog?.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className={`text-[10px] uppercase tracking-wider ${textMuted}`}>Event log</div>
                        <button
                          onClick={journifiSync.clearLog}
                          className={`text-[10px] px-2 py-0.5 rounded ${darkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                        >
                          Clear
                        </button>
                      </div>
                      <div className={`max-h-32 overflow-y-auto rounded font-mono text-[10px] leading-relaxed ${darkMode ? 'bg-black/30' : 'bg-gray-100'} p-2`}>
                        {journifiSync.syncLog.slice(0, 20).map((entry, idx) => (
                          <div key={`${entry.id}-${idx}`} className="flex gap-2">
                            <span className={textSubtle}>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                            <span className={
                              entry.type === 'success' ? 'text-emerald-400' :
                              entry.type === 'error' ? 'text-rose-400' :
                              entry.type === 'warning' ? 'text-amber-400' :
                              textMuted
                            }>
                              {entry.message}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── ACCOUNTS CARD (filter + AddOn diagnostic + mapping) ──────── */}
          {(availableAccounts.length > 0 || accounts.length > 0 || addonDisabledAccounts.length > 0) && (
            <div className={`rounded-xl border p-4 ${cardBg}`}>
              <SectionTitle
                icon={Users}
                color="purple"
                label="Accounts"
                badge={
                  unmappedCount > 0
                    ? `${unmappedCount} unmapped`
                    : selectedAccounts.length > 0
                      ? `${selectedAccounts.length} ${filterMode === 'exclude' ? 'excluded' : 'selected'}`
                      : null
                }
              />

              {/* Search box — applies to both filter list and mapping list */}
              {(availableAccounts.length > 4 || allMappableAccounts.length > 4) && (
                <div className={`mt-3 pt-3 border-t ${dividerBorder}`}>
                  <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border ${inputBg}`}>
                    <Search size={14} className={textMuted} />
                    <input
                      type="text"
                      value={accountSearchQuery}
                      onChange={(e) => setAccountSearchQuery(e.target.value)}
                      placeholder="Search accounts…"
                      className={`flex-1 bg-transparent text-xs outline-none ${text}`}
                    />
                    {accountSearchQuery && (
                      <button onClick={() => setAccountSearchQuery('')} className={textMuted}>
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* AddOn's disabledAccounts diagnostic — read-only display */}
              {addonSettingsLoaded && addonDisabledAccounts.length > 0 && (
                <div className={`mt-3 p-3 rounded-lg border ${darkMode ? 'bg-rose-500/[0.08] border-rose-400/30' : 'bg-rose-50 border-rose-200'}`}>
                  <div className="flex items-start gap-2">
                    <ShieldOff size={14} className={`shrink-0 mt-0.5 ${darkMode ? 'text-rose-300' : 'text-rose-600'}`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-semibold ${darkMode ? 'text-rose-200' : 'text-rose-800'}`}>
                        Blocked at NT AddOn level
                      </div>
                      <div className={`text-[11px] mt-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        These accounts have their fills dropped by JournifiSync NT before they reach this journal — no execution rows are written for them. Edit{' '}
                        <code className={`px-1 py-0.5 rounded font-mono text-[10px] ${darkMode ? 'bg-black/30' : 'bg-gray-100'}`}>{ADDON_SETTINGS_PATH}</code>{' '}
                        and restart NinjaTrader to change.
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {addonDisabledAccounts.map(name => (
                          <span
                            key={name}
                            className={`text-[10px] font-mono px-2 py-0.5 rounded ${darkMode ? 'bg-rose-500/20 text-rose-200' : 'bg-rose-100 text-rose-800'}`}
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ─── FILTER (collapsible) ───────────────────────────────── */}
              {availableAccounts.length > 0 && (
                <div className={`mt-3 pt-3 border-t ${dividerBorder}`}>
                  <button
                    type="button"
                    onClick={() => setShowAccountFilter(!showAccountFilter)}
                    className={`w-full flex items-center gap-2 text-left`}
                  >
                    <span className={`text-xs font-semibold ${text}`}>Import filter</span>
                    {selectedAccounts.length > 0 && (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${darkMode ? 'bg-purple-500/15 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                        {filterMode === 'exclude' ? `excluding ${selectedAccounts.length}` : `including ${selectedAccounts.length}`}
                      </span>
                    )}
                    <span className={`ml-auto ${textMuted}`}>
                      {showAccountFilter ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </span>
                  </button>

                  {showAccountFilter && (
                    <div className="mt-3 space-y-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`text-[11px] ${textMuted}`}>Mode:</span>
                        <PillToggle
                          value={filterMode}
                          onChange={setFilterMode}
                          options={[
                            { value: 'include', label: 'Include selected' },
                            { value: 'exclude', label: 'Exclude selected' },
                          ]}
                        />
                        <span className={`text-[10px] italic ${textSubtle}`}>
                          Only applies to manual Import below — live sync follows the AddOn&apos;s filter
                        </span>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedAccounts([...availableAccounts])}
                          className={`text-[11px] px-2 py-1 rounded ${darkMode ? 'bg-white/5 hover:bg-white/10 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`}
                        >
                          Select all
                        </button>
                        <button
                          onClick={() => setSelectedAccounts([])}
                          className={`text-[11px] px-2 py-1 rounded ${darkMode ? 'bg-white/5 hover:bg-white/10 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`}
                        >
                          Clear
                        </button>
                        <span className={`ml-auto text-[10px] self-center ${textSubtle}`}>
                          {filteredAvailableAccounts.length} of {availableAccounts.length} shown
                        </span>
                      </div>

                      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-y-auto rounded-md p-2 ${darkMode ? 'bg-black/20' : 'bg-white'}`}>
                        {filteredAvailableAccounts.length === 0 && (
                          <div className={`col-span-2 text-[11px] italic text-center py-3 ${textSubtle}`}>
                            No accounts match &ldquo;{accountSearchQuery}&rdquo;
</div>
                        )}
                        {filteredAvailableAccounts.map((account) => {
                          const checked = selectedAccounts.includes(account);
                          const isAddonBlocked = addonDisabledAccounts.includes(account);
                          return (
                            <label
                              key={account}
                              className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer transition-colors ${darkMode ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-100'}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedAccounts([...selectedAccounts, account]);
                                  else setSelectedAccounts(selectedAccounts.filter(a => a !== account));
                                }}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                              />
                              <span className={`text-xs font-mono truncate ${text}`} title={account}>
                                {account}
                              </span>
                              {isAddonBlocked && (
                                <ShieldOff size={11} className={`shrink-0 ml-auto ${darkMode ? 'text-rose-400' : 'text-rose-500'}`} title="Blocked at NT AddOn level" />
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ─── MAPPING (collapsible) ──────────────────────────────── */}
              {accounts.length > 0 && (
                <div className={`mt-3 pt-3 border-t ${dividerBorder}`}>
                  <button
                    type="button"
                    onClick={() => setShowAccountMapping(!showAccountMapping)}
                    className="w-full flex items-center gap-2 text-left"
                  >
                    <Link2 size={14} className={`shrink-0 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`} />
                    <span className={`text-xs font-semibold ${text}`}>Mapping</span>
                    {journifiSync?.accountMappings && Object.keys(journifiSync.accountMappings).length > 0 && (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${darkMode ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                        {Object.keys(journifiSync.accountMappings).length} mapped
                      </span>
                    )}
                    {unmappedCount > 0 && (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md inline-flex items-center gap-1 ${darkMode ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                        <AlertTriangle size={10} />
                        {unmappedCount} unmapped
                      </span>
                    )}
                    <span className={`ml-auto ${textMuted}`}>
                      {showAccountMapping ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </span>
                  </button>

                  {showAccountMapping && (
                    <div className="mt-3 space-y-3">
                      {unmappedCount > 0 && (
                        <div className={`p-2.5 rounded-lg border ${darkMode ? 'bg-amber-500/[0.08] border-amber-400/30 text-amber-200' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
                          <div className="flex items-start gap-2">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <div className="flex-1 text-[11px]">
                              <div className="font-semibold mb-0.5">
                                {unmappedCount} NT account{unmappedCount !== 1 ? 's' : ''} need{unmappedCount === 1 ? 's' : ''} mapping
                              </div>
                              <div>
                                Prop-firm resets (Bulenox, Take Profit Trader, etc.) issue new account numbers — map each one to its journal account below.
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {journifiSync?.unmappedAccounts?.map(name => (
                                  <span key={name} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] ${darkMode ? 'bg-amber-800/30' : 'bg-amber-100'}`}>
                                    {name}
                                    <button
                                      onClick={() => journifiSync?.dismissUnmappedAccount?.(name)}
                                      className="opacity-60 hover:opacity-100"
                                      title="Dismiss warning"
                                    >
                                      <X size={10} />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {journifiSync?.accountMappings && Object.keys(journifiSync.accountMappings).length > 0 && (
                        <div className="flex justify-end">
                          <button
                            onClick={() => journifiSync?.clearAccountMappings?.()}
                            className={`text-[10px] px-2 py-1 rounded ${darkMode ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                          >
                            Clear all mappings
                          </button>
                        </div>
                      )}

                      {/* Mapping rows */}
                      <div className="space-y-1 max-h-56 overflow-y-auto">
                        {filteredMappableAccounts.length === 0 && (
                          <div className={`text-[11px] italic text-center py-3 ${textSubtle}`}>
                            {accountSearchQuery
                              ? `No accounts match "${accountSearchQuery}"`
                              : 'No NinjaTrader accounts discovered yet. Add one manually below.'}
                          </div>
                        )}
                        {filteredMappableAccounts.map((ntAccount) => {
                          const currentMapping = journifiSync?.accountMappings?.[ntAccount] || '';
                          const mappedAccount = accounts.find(a => a.id === currentMapping);
                          const isFromFile = availableAccounts.includes(ntAccount);
                          const hasMapping = !!currentMapping;
                          const destIsNotActive = mappedAccount && mappedAccount.status && mappedAccount.status !== 'Active';
                          return (
                            <div
                              key={ntAccount}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded transition-colors ${
                                destIsNotActive
                                  ? darkMode ? 'bg-amber-500/[0.08] border border-amber-400/30' : 'bg-amber-50 border border-amber-300'
                                  : darkMode ? 'bg-white/[0.03] hover:bg-white/[0.06]' : 'bg-gray-50 hover:bg-gray-100'
                              }`}
                            >
                              <span className={`text-xs font-mono w-1/3 truncate ${text}`} title={`${ntAccount}${!isFromFile ? ' (manual)' : ''}`}>
                                {ntAccount}
                              </span>
                              <span className={`${textSubtle}`}>→</span>
                              <div className="flex-1">
                                <GlassSelect
                                  value={currentMapping}
                                  onChange={(v) => journifiSync?.setAccountMapping?.(ntAccount, v)}
                                  options={[
                                    { value: '', label: '— Auto-detect —' },
                                    ...accounts.map((account) => ({
                                      value: account.id,
                                      label: `${account.name}${account.status && account.status !== 'Active' ? ` (${account.status})` : ''}`,
                                    })),
                                  ]}
                                  darkMode={darkMode}
                                  size="sm"
                                />
                              </div>
                              {destIsNotActive ? (
                                <span className="shrink-0" title={`Mapped account is ${mappedAccount.status} — trades will be hidden by the Active-only filter`}>
                                  <AlertTriangle size={14} className={darkMode ? 'text-amber-400' : 'text-amber-500'} />
                                </span>
                              ) : mappedAccount ? (
                                <CheckCircle size={14} className={`shrink-0 ${darkMode ? 'text-emerald-400' : 'text-emerald-500'}`} />
                              ) : null}
                              {(hasMapping || !isFromFile) && (
                                <button
                                  onClick={() => journifiSync?.setAccountMapping?.(ntAccount, null)}
                                  className={`shrink-0 p-1 rounded ${darkMode ? 'text-gray-400 hover:text-rose-300 hover:bg-white/5' : 'text-gray-400 hover:text-rose-500 hover:bg-gray-100'}`}
                                  title={isFromFile
                                    ? 'Clear mapping — re-triggers auto-create on next execution'
                                    : 'Remove manual entry'}
                                >
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Manual add */}
                      <div className={`flex items-center gap-2 pt-2 border-t ${dividerBorder}`}>
                        <input
                          type="text"
                          placeholder="Add NinjaTrader account name…"
                          value={manualNtAccount}
                          onChange={(e) => setManualNtAccount(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && manualNtAccount.trim()) {
                              journifiSync?.setAccountMapping?.(manualNtAccount.trim(), '');
                              setManualNtAccount('');
                            }
                          }}
                          className={`flex-1 px-2 py-1 rounded text-xs border ${inputBg} ${text}`}
                        />
                        <button
                          onClick={() => {
                            if (manualNtAccount.trim()) {
                              journifiSync?.setAccountMapping?.(manualNtAccount.trim(), '');
                              setManualNtAccount('');
                            }
                          }}
                          disabled={!manualNtAccount.trim()}
                          className={`p-1.5 rounded text-xs border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                            manualNtAccount.trim()
                              ? darkMode
                                ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70'
                                : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
                              : darkMode
                                ? 'bg-white/5 border-white/10 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                          title="Add NT account"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className={`text-[10px] italic ${textSubtle}`}>
                        Tip: Auto-detect works when names contain similar text (e.g., &ldquo;Sim101&rdquo; matches &ldquo;Sim 101&rdquo;)
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── MANUAL IMPORT (date range + preview + import) ────────────── */}
          <div className={`rounded-xl border p-4 ${cardBg}`}>
            <SectionTitle
              icon={Download}
              color="blue"
              label="Manual Import"
              badge={preview ? `${preview.stats.tradesCreated} to import` : null}
            />
            <div className={`mt-3 pt-3 border-t ${dividerBorder} space-y-3`}>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-left"
              >
                <Calendar size={14} className={textMuted} />
                <span className={`text-xs font-semibold ${text}`}>Date range</span>
                {(dateRange.startDate || dateRange.endDate) && (
                  <span className={`text-[10px] font-mono ${textMuted}`}>
                    {dateRange.startDate || '…'} → {dateRange.endDate || '…'}
                  </span>
                )}
                <span className={`ml-auto ${textMuted}`}>
                  {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </span>
              </button>
              {showAdvanced && (
                <div className="grid grid-cols-2 gap-3 pl-6">
                  <div>
                    <label className={`block text-[11px] mb-1 ${textMuted}`}>Start date</label>
                    <input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                      className={`w-full px-2 py-1.5 rounded text-xs border ${inputBg} ${text}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-[11px] mb-1 ${textMuted}`}>End date</label>
                    <input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                      className={`w-full px-2 py-1.5 rounded text-xs border ${inputBg} ${text}`}
                    />
                  </div>
                </div>
              )}
              <div className={`text-[10px] italic ${textSubtle}`}>
                Leave date range empty to import everything in the sync folder. Filter selection above is applied.
              </div>
            </div>
          </div>

          {/* ── PREVIEW RESULTS ──────────────────────────────────────────── */}
          {preview && (
            <div className={`rounded-xl border p-4 ${cardBg}`}>
              <SectionTitle icon={CheckCircle} color="emerald" label="Preview" />
              <div className={`mt-3 pt-3 border-t ${dividerBorder}`}>
                {/* Stats grid */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-white/[0.04]' : 'bg-white'}`}>
                    <div className={`text-xl font-bold ${text}`}>{preview.stats.executionsFound}</div>
                    <div className={`text-[10px] uppercase tracking-wider ${textMuted}`}>Executions</div>
                  </div>
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
                    <div className={`text-xl font-bold ${darkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>{preview.stats.tradesCreated}</div>
                    <div className={`text-[10px] uppercase tracking-wider ${textMuted}`}>To Import</div>
                  </div>
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-amber-500/10' : 'bg-amber-50'}`}>
                    <div className={`text-xl font-bold ${darkMode ? 'text-amber-300' : 'text-amber-600'}`}>{preview.stats.duplicatesSkipped}</div>
                    <div className={`text-[10px] uppercase tracking-wider ${textMuted}`}>Duplicates</div>
                  </div>
                </div>

                {preview.stats.executionDuplicatesRemoved > 0 && (
                  <div className={`text-[11px] mb-2 px-2 py-1 rounded ${darkMode ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
                    {preview.stats.executionDuplicatesRemoved} duplicate execution(s) in source files auto-removed
                  </div>
                )}

                {preview.stats.duplicatesSkipped > 0 && (
                  <div className={`text-[11px] mb-2 px-2 py-1 rounded ${darkMode ? 'bg-white/[0.03] text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                    <span className="font-medium">Dedup:</span>{' '}
                    {preview.stats.duplicatesByExecId > 0 && <span>{preview.stats.duplicatesByExecId} exec-id · </span>}
                    {preview.stats.duplicatesBySignature > 0 && <span>{preview.stats.duplicatesBySignature} signature · </span>}
                    {preview.stats.duplicatesInBatch > 0 && <span>{preview.stats.duplicatesInBatch} batch</span>}
                  </div>
                )}

                {/* Repair candidates */}
                {preview.repairs && preview.repairs.length > 0 && (
                  <div className={`mb-3 p-3 rounded-lg border ${darkMode ? 'bg-amber-500/[0.08] border-amber-400/30' : 'bg-amber-50 border-amber-300'}`}>
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle size={14} className={`mt-0.5 shrink-0 ${darkMode ? 'text-amber-300' : 'text-amber-600'}`} />
                      <div className="flex-1">
                        <div className={`text-xs font-medium ${darkMode ? 'text-amber-200' : 'text-amber-800'}`}>
                          {preview.repairs.length} trade{preview.repairs.length === 1 ? '' : 's'} need account repair
                        </div>
                        <div className={`text-[11px] mt-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          These trades are already in your journal but have wrong (or missing) accounts. Repair reassigns them without re-importing.
                        </div>
                      </div>
                    </div>
                    <div className={`max-h-32 overflow-y-auto rounded text-[10px] ${darkMode ? 'bg-black/30' : 'bg-white'}`}>
                      {preview.repairs.map((r, idx) => {
                        const currentLabel = r.currentAccountId
                          ? (accounts.find(a => a.id === r.currentAccountId)?.name || r.currentAccountId)
                          : '(none)';
                        const proposedLabel = r.proposedAccountId
                          ? (accounts.find(a => a.id === r.proposedAccountId)?.name || r.proposedAccountId)
                          : '(still unmapped)';
                        return (
                          <div key={r.tradeId || idx} className={`px-2 py-1 border-b flex items-center justify-between gap-2 ${dividerBorder}`}>
                            <span className={`truncate ${text}`}>
                              {r.tradeSummary?.date} · {r.tradeSummary?.symbol} {r.tradeSummary?.direction === 'long' ? 'L' : 'S'} ×{r.tradeSummary?.quantity}
                            </span>
                            <span className={`whitespace-nowrap ${textMuted}`}>
                              {currentLabel} <span className="text-amber-400">→</span> {proposedLabel}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {onRepairTrades && preview.repairs.some(r => r.proposedAccountId) && (
                      <button
                        onClick={() => {
                          const valid = preview.repairs.filter(r => r.proposedAccountId);
                          onRepairTrades(valid);
                          setPreview(prev => prev ? { ...prev, repairs: [], stats: { ...prev.stats, repairCandidates: 0 } } : prev);
                        }}
                        className={`mt-2 w-full px-3 py-1.5 rounded text-xs font-medium border transition-[background-color,border-color,box-shadow,color] duration-150 ${
                          darkMode
                            ? 'bg-amber-500/25 border-amber-400/50 text-amber-50 ring-1 ring-inset ring-amber-400/25 hover:bg-amber-500/35 hover:border-amber-400/70 hover:shadow-[0_0_14px_rgba(245,158,11,0.25)]'
                            : 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200 hover:border-amber-400'
                        }`}
                      >
                        Repair {preview.repairs.filter(r => r.proposedAccountId).length} trade(s)
                      </button>
                    )}
                  </div>
                )}

                {/* Trade summaries */}
                {preview.tradeSummaries && preview.tradeSummaries.length > 0 && (
                  <div className={`max-h-44 overflow-y-auto border rounded ${dividerBorder}`}>
                    <table className="w-full text-xs">
                      <thead className={`sticky top-0 ${darkMode ? 'bg-gray-900/95' : 'bg-gray-100'}`}>
                        <tr className={textMuted}>
                          <th className="p-2 text-left font-semibold">Date</th>
                          <th className="p-2 text-left font-semibold">Symbol</th>
                          <th className="p-2 text-left font-semibold">Dir</th>
                          <th className="p-2 text-right font-semibold">Qty</th>
                          <th className="p-2 text-right font-semibold">P&L</th>
                          <th className="p-2 text-left font-semibold">ATM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.tradeSummaries.slice(0, 10).map((trade, idx) => (
                          <tr key={idx} className={`border-t ${dividerBorder}`}>
                            <td className={`p-2 ${text}`}>{trade.date}</td>
                            <td className={`p-2 font-mono ${text}`}>{trade.symbol}</td>
                            <td className={`p-2 ${trade.direction === 'long' ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {trade.direction === 'long' ? 'L' : 'S'}
                            </td>
                            <td className={`p-2 text-right ${text}`}>{trade.quantity}</td>
                            <td className={`p-2 text-right font-mono ${parseFloat(trade.pnl) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              ${parseFloat(trade.pnl).toFixed(2)}
                            </td>
                            <td className={`p-2 truncate max-w-[140px] ${textMuted}`}>{trade.atmStrategy || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {preview.tradeSummaries.length > 10 && (
                      <div className={`p-2 text-center text-[11px] ${textMuted}`}>
                        … and {preview.tradeSummaries.length - 10} more
                      </div>
                    )}
                  </div>
                )}

                {preview.newAccounts && preview.newAccounts.length > 0 && (
                  <div className={`mt-3 p-2.5 rounded-lg border ${darkMode ? 'bg-amber-500/[0.08] border-amber-400/30 text-amber-200' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
                    <div className="flex items-center gap-2 font-medium text-xs mb-1">
                      <AlertCircle size={14} />
                      New accounts detected
                    </div>
                    <div className={`text-[11px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {preview.newAccounts.map(a => a.suggestedName).join(', ')}
                    </div>
                    <div className={`text-[10px] mt-0.5 ${textMuted}`}>
                      These will need to be created or mapped to existing accounts.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── IMPORT RESULT ────────────────────────────────────────────── */}
          {importResult && (
            <div className={`rounded-xl border p-4 ${darkMode ? 'bg-emerald-500/[0.08] border-emerald-400/30' : 'bg-emerald-50 border-emerald-300'}`}>
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className={darkMode ? 'text-emerald-400' : 'text-emerald-600'} />
                <span className={`text-sm font-semibold ${darkMode ? 'text-emerald-200' : 'text-emerald-800'}`}>Import complete</span>
              </div>
              <div className={`text-xs mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Imported {importResult.stats.tradesCreated} trade(s)
                {importResult.stats.duplicatesSkipped > 0 && ` · ${importResult.stats.duplicatesSkipped} duplicates skipped`}
              </div>
            </div>
          )}

          {/* ── ERRORS ───────────────────────────────────────────────────── */}
          {error && (
            <div className={`rounded-xl border p-4 ${darkMode ? 'bg-rose-500/[0.08] border-rose-400/30' : 'bg-rose-50 border-rose-300'}`}>
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={16} className={darkMode ? 'text-rose-400' : 'text-rose-600'} />
                <span className={`text-sm font-semibold ${darkMode ? 'text-rose-200' : 'text-rose-800'}`}>Errors</span>
              </div>
              <div className={`text-xs whitespace-pre-wrap ${darkMode ? 'text-rose-300' : 'text-rose-700'}`}>{error}</div>
            </div>
          )}
        </div>

        {/* ── FOOTER ─────────────────────────────────────────────────────── */}
        <div className={`flex items-center justify-between px-5 py-3 border-t ${dividerBorder} ${darkMode ? 'bg-black/20' : 'bg-gray-50/50'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm transition-colors duration-200 ${
              darkMode ? 'bg-white/5 hover:bg-white/10 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
            }`}
          >
            {importResult ? 'Close' : 'Cancel'}
          </button>
          {!importResult && (
            <div className="flex gap-2">
              <button
                onClick={handlePreview}
                disabled={isLoading || !syncStatus?.exists}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
                  darkMode
                    ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70 hover:shadow-[0_0_16px_rgba(59,130,246,0.25)]'
                    : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
                }`}
              >
                <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
                Preview
              </button>
              <button
                onClick={handleImport}
                disabled={isLoading || !syncStatus?.exists || !preview}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
                  darkMode
                    ? 'bg-emerald-500/25 border-emerald-400/50 text-emerald-50 ring-1 ring-inset ring-emerald-400/25 hover:bg-emerald-500/35 hover:border-emerald-400/70 hover:shadow-[0_0_18px_rgba(16,185,129,0.3)]'
                    : 'bg-emerald-100 border-emerald-300 text-emerald-800 hover:bg-emerald-200 hover:border-emerald-400'
                }`}
              >
                {isLoading ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
                Import {preview ? `(${preview.stats.tradesCreated})` : ''}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JournifiSyncModal;
